import { toast } from "sonner";
import { FileCode, ShieldCheck, CheckCircle2, AlertCircle, ClipboardSignature } from "lucide-react";
import api, { formatApiError, API } from "../lib/api";
import { Button } from "./ui/button";
import { Link } from "react-router-dom";

export default function InvoicePanel({ order, id, isWinner, einvCfg, testingConn, onTestConnection, onReload }) {
  if (!["awarded", "paid", "delivered"].includes(order.status)) return null;
  const slipLabel = order.status === "delivered" ? "Bolla di consegna (PDF)" : "Bolla di consegna da firmare (PDF)";
  const showSlip = isWinner || order.status === "delivered";
  if (order.status === "awarded") {
    if (!isWinner) return null;
    return (
      <div className="mt-3" data-testid="delivery-note-awarded">
        <a href={`${API}/orders/${id}/delivery-note.pdf`} target="_blank" rel="noreferrer" className="w-full inline-flex items-center justify-center rounded-full bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800" data-testid="delivery-note-button">
          <ClipboardSignature className="w-4 h-4 mr-2" />{slipLabel}
        </a>
        <div className="mt-1.5 text-[11px] text-slate-500 text-center">Stampala e portala in consegna: la fattura sarà disponibile dopo il pagamento.</div>
      </div>
    );
  }
  return (
    <>
      {order.einvoice?.fic_transmitted_at && (
        <div className="mt-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-sm" data-testid="fic-transmitted-badge">
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-700 flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" /> Fatture in Cloud · Trasmesso a SdI
          </div>
          <div className="mt-1 text-xs text-emerald-900">Documento #{order.einvoice.fic_document_id} · {new Date(order.einvoice.fic_transmitted_at).toLocaleString("it-IT")}</div>
        </div>
      )}
      {order.einvoice?.aruba_transmitted_at && (
        <div className="mt-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-sm" data-testid="aruba-transmitted-badge">
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-700 flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" /> Aruba · Trasmesso a SdI
          </div>
          <div className="mt-1 text-xs text-emerald-900 break-all">Upload {order.einvoice.aruba_upload_id} · {new Date(order.einvoice.aruba_transmitted_at).toLocaleString("it-IT")}</div>
        </div>
      )}
      {showSlip && (
        <a href={`${API}/orders/${id}/delivery-note.pdf`} target="_blank" rel="noreferrer" className="mt-3 w-full inline-flex items-center justify-center rounded-full bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800" data-testid="delivery-note-button">
          <ClipboardSignature className="w-4 h-4 mr-2" />{slipLabel}
        </a>
      )}
      <a href={`${API}/orders/${id}/invoice.xml`} target="_blank" rel="noreferrer" className="mt-3 w-full inline-flex items-center justify-center rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-50" data-testid="download-invoice-button">
        <FileCode className="w-4 h-4 mr-2" />Scarica fattura SdI (XML)
      </a>
      <a href={`${API}/orders/${id}/invoice-preview.pdf`} target="_blank" rel="noreferrer"
        onClick={(e) => {
          if (!order.invoice_data?.customer_billing_complete) {
            e.preventDefault();
            toast.error(isWinner
              ? "Il cliente non ha completato i dati di fatturazione (P.IVA + SDI o PEC)"
              : "Completa i dati di fatturazione (P.IVA + Codice SDI o PEC) nella tua dashboard");
          }
        }}
        className="mt-2 w-full inline-flex items-center justify-center rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-50" data-testid="preview-invoice-button">
        <FileCode className="w-4 h-4 mr-2" />Anteprima fattura (PDF)
      </a>
      {!order.invoice_data?.customer_billing_complete && (
        <Link to="/ristorante" className="mt-2 block text-center text-xs text-amber-700 underline" data-testid="billing-missing-hint">
          {isWinner ? "Dati fiscali del cliente incompleti: la fattura non è disponibile" : "Dati di fatturazione incompleti → completa P.IVA e SDI/PEC"}
        </Link>
      )}
      {isWinner && (
        <>
          <div className="mt-3 p-2 rounded-lg bg-slate-50 border border-slate-200 text-xs">
            <div className="font-mono uppercase tracking-widest text-slate-500 mb-1">Provider SdI configurati</div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${einvCfg.fic?.enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-500"}`} data-testid="fic-config-badge">
                {einvCfg.fic?.enabled ? <CheckCircle2 className="w-2.5 h-2.5" /> : <AlertCircle className="w-2.5 h-2.5" />} FIC
              </span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${einvCfg.aruba?.enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-500"}`} data-testid="aruba-config-badge">
                {einvCfg.aruba?.enabled ? <CheckCircle2 className="w-2.5 h-2.5" /> : <AlertCircle className="w-2.5 h-2.5" />} Aruba
              </span>
              <button onClick={onTestConnection} disabled={testingConn} className="ml-auto text-[10px] font-mono uppercase tracking-widest text-slate-600 hover:text-slate-900 underline" data-testid="test-einvoice-button">
                {testingConn ? "test..." : "Test connessione"}
              </button>
            </div>
          </div>
          <Button variant="outline" className="mt-2 w-full rounded-full" disabled={!einvCfg.fic?.enabled || !!order.einvoice?.fic_transmitted_at}
            onClick={async () => {
              try { const { data } = await api.post(`/orders/${id}/invoice/transmit`); toast.success(`Fattura FIC trasmessa · #${data.number || data.document_id}`); onReload(); }
              catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
            }} data-testid="transmit-invoice-button">
            <FileCode className="w-4 h-4 mr-2" />{order.einvoice?.fic_transmitted_at ? "Già trasmessa FIC" : "Trasmetti a Fatture in Cloud"}
          </Button>
          <Button variant="outline" className="mt-2 w-full rounded-full" disabled={!einvCfg.aruba?.enabled || !!order.einvoice?.aruba_transmitted_at}
            onClick={async () => {
              try { const { data } = await api.post(`/orders/${id}/invoice/transmit-aruba`); toast.success(`Fattura Aruba trasmessa · ${data.upload_id}`); onReload(); }
              catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
            }} data-testid="transmit-invoice-aruba-button">
            <FileCode className="w-4 h-4 mr-2" />{order.einvoice?.aruba_transmitted_at ? "Già trasmessa Aruba" : "Trasmetti a Aruba"}
          </Button>
        </>
      )}
    </>
  );
}
